import React, { useRef, useEffect } from 'react';

const NeuralNetworkBackground = ({ className, color = 'rgb(0, 255, 0)', mobileNodes, webNodes }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;

    const getNumNodes = () => {
      if (mobileNodes !== undefined && webNodes !== undefined) {
        return window.innerWidth < 768 ? mobileNodes : webNodes;
      }
      return window.innerWidth < 768 ? 10 : 20; // Global default
    };

    const maxDistance = 100; // Max distance for lines between nodes

    let currentNumNodes = getNumNodes();
    let nodes = [];

    const initializeNodes = () => {
      nodes = []; // Clear existing nodes
      currentNumNodes = getNumNodes();
      for (let i = 0; i < currentNumNodes; i++) {
        nodes.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.5, // Small velocity
          vy: (Math.random() - 0.5) * 0.5,
        });
      }
    };

    // Set canvas size to match its rendered dimensions
    const updateCanvasDimensions = () => {
      if (canvasRef.current) {
        canvas.width = canvasRef.current.offsetWidth;
        canvas.height = canvasRef.current.offsetHeight;
        initializeNodes(); // Re-initialize nodes on dimension change
      }
    };

    updateCanvasDimensions(); // Initial set and node initialization

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Nodes color and opacity
      ctx.fillStyle = `${color.replace('rgb', 'rgba').replace(')', ', 0.7)')}`; // Use color prop with opacity

      nodes.forEach(node => {
        // Update node position
        node.x += node.vx;
        node.y += node.vy;

        // Bounce off edges
        if (node.x < 0 || node.x > canvas.width) node.vx *= -1;
        if (node.y < 0 || node.y > canvas.height) node.vy *= -1;

        // Draw node
        ctx.beginPath();
        ctx.arc(node.x, node.y, 2, 0, Math.PI * 2); // Original node size
        ctx.fill();

        // Draw connections to other nodes
        nodes.forEach(otherNode => {
          const distance = Math.sqrt(
            (node.x - otherNode.x) ** 2 +
            (node.y - otherNode.y) ** 2
          );

          if (distance < maxDistance) {
            // Random opacity for lines to simulate connecting/disconnecting
            const opacity = Math.random() * 0.7; // Increased subtle opacity
            ctx.strokeStyle = `${color.replace('rgb', 'rgba').replace(')', ', ' + opacity + ')')}`; // Use color prop with random opacity
            ctx.lineWidth = 0.5; // Original thin lines
            ctx.beginPath();
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(otherNode.x, otherNode.y);
            ctx.stroke();
          }
        });
      });

      animationFrameId = requestAnimationFrame(draw);
    };

    draw();

    // Handle window resize
    const handleResize = () => {
      updateCanvasDimensions(); // Update dimensions and re-initialize nodes on resize
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
    };
  }, [color, mobileNodes, webNodes]); // Re-run effect if color or node counts change

  return <canvas ref={canvasRef} className={className} />;
};

export default NeuralNetworkBackground;
